import { computed, ref } from 'vue'
import { useToast } from '@/composables/useToast.ts'
import CustomerFeedbackService, { type FeedbackSubmission, type ValidationError } from '@/services/CustomerFeedbackService.ts'
import { AxiosError } from 'axios'

export interface FeedbackFormData {
  rating: number | null
  supportSession: string // 'yes' or 'no'
  subject: string
  feedback: string
  attachedFiles: File[]
}

export interface FeedbackSubject {
  value: string
  label: string
}

export const useFeedbackForm = (uploadsAllowed = false) => {
  // Form data
  const formData = ref<FeedbackFormData>({
    rating: null,
    supportSession: '',
    subject: '',
    feedback: '',
    attachedFiles: [],
  })

  // Form state
  const isSubmitting = ref(false)
  const submitError = ref<string | null>(null)
  const submitSuccess = ref(false)

  // Subject options matching backend enum
  const subjectOptions: FeedbackSubject[] = [
    { value: 'compliment', label: 'Compliment' },
    { value: 'feature_request', label: 'Feature request' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'bug_report', label: 'Report a bug' },
    { value: 'other', label: 'Other' },
  ]

  // Form validation matching backend rules
  const isFormValid = computed(() => {
    const validSubjectValues = subjectOptions.map((option) => option.value)
    return (
      formData.value.rating !== null &&
      formData.value.rating >= 1 &&
      formData.value.rating <= 5 &&
      formData.value.supportSession !== '' &&
      formData.value.subject !== '' &&
      validSubjectValues.includes(formData.value.subject) &&
      formData.value.feedback.trim().length >= 10 &&
      formData.value.feedback.trim().length <= 5000
    )
  })

  // Character count for feedback field
  const feedbackCharCount = computed(() => formData.value.feedback.length)
  const feedbackCharRemaining = computed(() => 5000 - feedbackCharCount.value)
  const isFeedbackValid = computed(() => {
    const length = formData.value.feedback.trim().length
    return length >= 10 && length <= 5000
  })

  // Form handlers
  const setRating = (value: number) => {
    formData.value.rating = value
  }

  const handleSubjectSelected = (item: { label: string; value: string }) => {
    formData.value.subject = item.value
  }

  // File handling - simplified to just update the reactive array
  const handleFilesChanged = (files: File[]) => {
    formData.value.attachedFiles = files
  }

  // Reset form
  const resetForm = () => {
    formData.value = {
      rating: null,
      supportSession: '',
      subject: '',
      feedback: '',
      attachedFiles: [],
    }
    submitError.value = null
    submitSuccess.value = false
  }

  // Form submission
  const handleSubmit = async () => {
    const { errorToast, successToast } = useToast()
    if (!isFormValid.value) return

    isSubmitting.value = true
    submitError.value = null
    submitSuccess.value = false

    try {
      const payload: FeedbackSubmission = {
        rating: formData.value.rating!,
        support_session: formData.value.supportSession === 'yes',
        subject: formData.value.subject,
        feedback: formData.value.feedback.trim(),
      }

      const response = await CustomerFeedbackService.submitFeedback(payload)
      
      if (response.data) {
        submitSuccess.value = true
        successToast('Thank you for your feedback! We appreciate your input.')
        resetForm()
      }
    } catch (error: unknown) {
      console.error('Error submitting feedback:', error)
      
      if (error instanceof AxiosError) {
        // Handle validation errors (422)
        if (error.response?.status === 422) {
          const validationError = error.response.data as ValidationError
          const errorMessages = Object.values(validationError.errors || {})
            .flat()
            .join(', ')
          submitError.value = errorMessages || 'Please check your input and try again.'
          errorToast(submitError.value)
        }
        // Handle authentication errors (401)
        else if (error.response?.status === 401) {
          submitError.value = 'You must be logged in to submit feedback.'
          errorToast(submitError.value)
        }
        // Handle other API errors
        else {
          const message = error.response?.data?.message || 'Failed to submit feedback. Please try again.'
          submitError.value = message
          errorToast(message)
        }
      } else {
        submitError.value = 'An unexpected error occurred. Please try again.'
        errorToast(submitError.value)
      }
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    // Data
    formData,
    subjectOptions,
    uploadsAllowed,

    // State
    isSubmitting,
    submitError,
    submitSuccess,

    // Computed
    isFormValid,
    feedbackCharCount,
    feedbackCharRemaining,
    isFeedbackValid,

    // Functions
    setRating,
    handleSubjectSelected,
    handleFilesChanged,
    handleSubmit,
    resetForm,
  }
}
